import { useEffect, useRef, useState } from 'react';

import ChevronDownIcon from 'components/icons/ChevronDownIcon';
import ChevronUpIcon from 'components/icons/ChevronUpIcon';

import './CustomSelect.css';

interface CustomSelectProps {
  value: string | string[];
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  onChange: (value: string | string[]) => void;
  multiSelect?: boolean;
  searchable?: boolean;
}

function CustomSelect({
  value,
  options,
  placeholder = 'Select...',
  disabled = false,
  isInvalid = false,
  onChange,
  multiSelect = false,
  searchable = false,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm('');
    }
  };

  const handleSingleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleMultiSelect = (option: string, checked: boolean) => {
    const currentValue = Array.isArray(value) ? value : [];
    if (checked) {
      onChange([...currentValue, option]);
    } else {
      onChange(currentValue.filter(v => v !== option));
    }
  };

  const filteredOptions = searchable
    ? options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const getDisplayValue = () => {
    if (multiSelect) {
      const selectedCount = Array.isArray(value) ? value.length : 0;
      if (selectedCount === 0) return placeholder;
      return `${selectedCount} selected`;
    }
    return value || placeholder;
  };

  const isPlaceholder = multiSelect
    ? !Array.isArray(value) || value.length === 0
    : !value;

  return (
    <div className="custom-select-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`custom-select-button ${isInvalid ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span
          className={
            isPlaceholder ? 'custom-select-placeholder' : 'custom-select-value'
          }
        >
          {getDisplayValue()}
        </span>
        <span className="custom-select-arrow">
          {isOpen ? (
            <ChevronUpIcon size={12} color="#6c757d" />
          ) : (
            <ChevronDownIcon size={12} color="#6c757d" />
          )}
        </span>
      </button>

      {isOpen && (
        <div className="custom-select-menu">
          {searchable && (
            <div className="custom-select-search">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          )}

          {filteredOptions.length === 0 ? (
            <div className="custom-select-no-results">No results found</div>
          ) : multiSelect ? (
            filteredOptions.map(option => {
              const isChecked = Array.isArray(value) && value.includes(option);
              return (
                <label key={option} className="custom-select-checkbox-option">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={e => handleMultiSelect(option, e.target.checked)}
                  />
                  <span>{option}</span>
                </label>
              );
            })
          ) : (
            filteredOptions.map(option => (
              <div
                key={option}
                role="option"
                aria-selected={value === option}
                tabIndex={0}
                className={`custom-select-option ${value === option ? 'selected' : ''}`}
                onClick={() => handleSingleSelect(option)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSingleSelect(option);
                  }
                }}
              >
                {option}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default CustomSelect;
